import React from 'react';
import { Phone, Clock, MapPin } from 'lucide-react';

const ThreeDaySerengeti = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-[#2f4f2f] py-4 px-6 border-b border-[#9acd32]/20">
        <h1 className="text-2xl font-bold">3 DAYS SERENGETI NATIONAL PARK SAFARI</h1>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-[#2f4f2f] mb-6">
          3 Days Serengeti National Park Safari – Tanzania Adventure Wildlife Tour
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="md:col-span-2">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                Experience the adventures of a <strong>3 Days Serengeti National Park Safari</strong>, beginning with a warm transfer from Arusha through stunning landscapes culminating in the breathtaking Serengeti ecosystem. Your adventure guide ensures that each wildlife encounter becomes a cherished memory, while every landscape becomes a breathtaking backdrop for your Tanzania wildlife safari.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Explore vast expanses teeming with wildebeest, zebra, and antelopes, and spot predators such as lions, cheetahs, and leopards. Immerse yourself in the heart of Africa, captivated by the world-renowned Serengeti landscape, home to the annual migration of 1.5 million wildebeest.
              </p>
            </div>

            {/* Placeholder for Safari Image */}
            <div className="mb-8">
              <div className="bg-[#f5f5f5] h-64 rounded-lg flex items-center justify-center text-gray-500 text-sm">
                <img 
                src="/CUBS.JPG" 
                alt="3 Days Tanzania Wildlife Safari" 
                className="w-full h-48 object-cover"
              />
                
              </div>
              <p className="text-center text-xs text-gray-500 mt-2">
                Serengeti National Park Safari - Hyena with cubs
              </p>
            </div>

            {/* Itinerary */}
            <h3 className="text-2xl font-bold text-[#2f4f2f] mb-6">
              Itinerary for the 3 Days Serengeti National Park Safari
            </h3>

            <div className="space-y-6">
              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 1: Transfer to Serengeti National Park</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Your safari begins with an early morning pickup from your accommodation in Arusha. You will travel through the Ngorongoro Conservation Area, passing by Olduvai Gorge, a significant archaeological site where Dr. Louis and Mary Leakey discovered early human remains. Along the way, you may spot eland and zebra. Breakfast and lunch will be provided en route in your safari vehicle, leading to an afternoon game drive in the Serengeti, where you'll encounter its vibrant wildlife.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Nyota Luxury Camp
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 2: Full Day Game Drive in Serengeti National Park</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Start your day with an early breakfast, followed by a thrilling full-day game drive in the Serengeti. Explore the park's vast plains, tracking the Big Five and other magnificent animals such as gazelles, zebras, and thousands of wildebeest. The Serengeti's expansive grasslands, rocky outcrops, and distant plains offer exceptional wildlife viewing, especially during the Great Migration between the Serengeti and Masai Mara.
                </p>
                <p className="text-sm font-semibold text-[#2f4f2f]">
                  Accommodation: Nyota Luxury Camp
                </p>
              </div>

              <div className="p-4 border border-[#9acd32]/20 rounded-lg">
                <h4 className="text-lg font-bold text-[#2f4f2f] mb-2">Day 3: Final Game Drive in Serengeti National Park & Transfer to Arusha</h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  After an early breakfast, embark on a final game drive through the Serengeti’s vast expanses for more wildlife viewing opportunities. Following the morning game drive, begin your scenic transfer back to Arusha, arriving by late afternoon at your chosen drop-off location, such as your hotel or the airport.
                </p>
              </div>
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Inclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Accommodation on a safari vehicle with a pop-up roof
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Game drives as per itinerary
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Entrance fees to National Parks
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    En-route lunches
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Bottled water
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    Professional English-speaking driver guide
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#9acd32] mr-2">•</span>
                    English driver guides
                  </li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">Exclusions</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Visa Fees & flights
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Optional activities
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Tips for guides
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Laundry
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International vaccinations & tests
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Health test requirements
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    International travel insurance
                  </li>
                  <li className="flex items-start">
                    <span className="text-[#228b22] mr-2">•</span>
                    Items of personal nature
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-6">
              <div className="bg-white border border-[#9acd32]/20 rounded-lg p-4 mb-6">
                <h3 className="text-lg font-bold text-[#2f4f2f] mb-2">3 Days Serengeti National Park Safari</h3>
                <ul className="text-sm text-gray-700 mb-4 space-y-2">
                  <li className="flex items-center">
                    <Clock className="mr-2 h-4 w-4 text-gray-600" />
                    Duration: 3 Days, 2 Nights safari
                  </li>
                  <li className="flex items-center">
                    <MapPin className="mr-2 h-4 w-4 text-gray-600" />
                    Regions / Arusha
                  </li>
                  <li className="text-xs text-gray-600 ml-6">
                    Hotel pickup - Your side returned to your hotel
                  </li>
                </ul>
                <button className="w-full bg-[#2f4f2f] hover:bg-[#228b22] text-white font-semibold py-3 px-4 rounded-lg mb-4 transition-colors">
                  Plan Your Trip Now
                </button>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Call/WhatsApp for a Free Quote</p>
                  <p className="text-lg font-bold text-[#2f4f2f]">
                    <Phone className="inline mr-2" size={20} />
                    <a href="tel:+255753182737" className="text-[#9acd32] hover:text-[#228b22] transition-colors">+255 753 182 737</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreeDaySerengeti;